import {useCallback} from 'react';

import {assert} from 'Asserts';
import DocumentStore from 'notebook/documents/DocumentStore';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import * as SpreadsheetEvalUtil from 'notebook/tables/spreadsheets/SpreadsheetEvalUtil';
import {updateTableInNote} from 'notebook/tables/store/actions/TableActions';
import {HydratedTableContext} from 'notebook/tables/store/contexts/HydratedTableContext';
import {TableNoteContext} from 'notebook/tables/store/contexts/TableNoteContext';
import {_updateCellTransform as updateCellMutation} from 'notebook/tables/store/logic/mutations/updateCell';
import {trackCrossStepReferenceChange} from 'notebook/tables/util/analytics/CrossStepReferenceAnalytics';
import {getWorksheetIdFromNamespace} from 'notebook/tables/util/WorksheetTableNamespaceUtil';

/**
 * This hook encapsulates the actions, and applies the approriate contexts.
 *
 * This must be used with a TableNoteContext Provider (like DocumentNoteProvider) and a HydratedTableProvider.
 */
export function useTableActions(): {
  updateCell: (index: TableIndex, cellData: Partial<Cell> & {text: string}) => Promise<void>;
  setActiveFormulaText: (index: TableIndex, text: string, cursorPosition: number, context: string) => void;
} {
  const {fileId, tableNamespace, getNote, updateNote} = TableNoteContext.useContext();
  const {tableName, selectTable} = HydratedTableContext.useContext();

  // This is explicitly not exported, since we should not be updating the entire table directly.  There should
  // always be more specific actions perform a known operation.
  const updateTable = useCallback(
    async (updatedTable: Table) => {
      const note = getNote();
      const updatedNote = await updateTableInNote(note, updatedTable);
      updateNote(updatedNote, {performTableEvaluation: true});
    },
    [getNote, updateNote]
  );

  const updateCell = useCallback(
    async (index: TableIndex, cellData: Partial<Cell> & {text: string}) => {
      const note = getNote();
      const table = selectTable(note);
      assert(table != null, `Table not found when updating cell`);

      const oldCellText = table.table[index[0]][index[1]]?.text ?? '';
      const worksheetId = getWorksheetIdFromNamespace(tableNamespace);
      const worksheetInfo = worksheetId
        ? {worksheetId, stepCount: DocumentStore.getRelatedDocumentIds(fileId).length}
        : null;
      trackCrossStepReferenceChange(oldCellText, cellData.text, worksheetInfo);

      // TODO(BNCH-137321): propagate tableOp
      const {table: updatedTable} = updateCellMutation(table, index, cellData);
      await updateTable(updatedTable);
    },
    [fileId, getNote, selectTable, tableNamespace, updateTable]
  );

  const setActiveFormulaText = useCallback(
    (index: TableIndex, text: string, cursorPosition: number, cid: string) => {
      SpreadsheetEvalUtil.setMultiEditingForTable({tableName, documentName: null}, tableNamespace, fileId, {
        editing: true,
        index,
        text,
        typedValue: null,
        selection: {
          cid,
          start: cursorPosition,
          end: cursorPosition,
        },
      });
    },
    [fileId, tableName, tableNamespace]
  );

  return {
    updateCell,
    setActiveFormulaText,
  };
}
